import { cn } from 'lib/utils'
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react'
import { useCallback, useState, type MouseEvent } from 'react'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { renderComposition } from '../api/composition-api'
import { useCreativeStore } from '../store/creative-store'
import { useIdeaStore } from '../store/idea-store'
import {
  editsRenderStateFromContent,
  mergeEditsRenderStates,
  type EditsRenderState,
} from '../utils/edits-render-state'

export { editsRenderStateFromContent, type EditsRenderState }

/**
 * Non-hook snapshot of the dual-store state, for imperative call sites (e.g. the
 * composer's self-heal render on provider unmount). A creative can live in either
 * store depending on which page hydrated it. True for BOTH pending states: a failed
 * render is still a master that lags the edits, and the self-heal is a free retry.
 */
export function editsApplyingNow(creativeId: string): boolean {
  return editsRenderStateNow(creativeId) !== null
}

function editsRenderStateNow(creativeId: string): EditsRenderState {
  return mergeEditsRenderStates(
    editsRenderStateFromContent(
      useCreativeStore.getState().creatives.find(c => c.id === creativeId)?.content
    ),
    editsRenderStateFromContent(
      useIdeaStore.getState().ideas.find(i => i.primaryCreative?.id === creativeId)?.primaryCreative
        ?.content
    )
  )
}

/**
 * Live edit-pipeline state for a creative. Checks both stores: grid pages hydrate
 * the creative store, Home hydrates the idea store; the Pusher content patch keeps
 * both in sync. A 'failed' in either store wins over an 'applying' in the other.
 */
export function useEditsRenderState(creativeId?: string | null): EditsRenderState {
  const fromCreatives = useCreativeStore(s =>
    creativeId
      ? editsRenderStateFromContent(s.creatives.find(c => c.id === creativeId)?.content)
      : null
  )
  const fromIdeas = useIdeaStore(s =>
    creativeId
      ? editsRenderStateFromContent(
          s.ideas.find(i => i.primaryCreative?.id === creativeId)?.primaryCreative?.content
        )
      : null
  )
  return mergeEditsRenderStates(fromCreatives, fromIdeas)
}

/**
 * True while the master lags the composition edits, in either pending state.
 * Consumers that only need "don't present the stale master as current" keep using
 * this; the badge itself tells applying from failed.
 */
export function useEditsApplying(creativeId?: string | null): boolean {
  return useEditsRenderState(creativeId) !== null
}

/**
 * Re-render the stored document for a creative (free, no credit) and flip the
 * failed marker back to "applying" optimistically. The server clears the marker
 * before rendering and re-stamps it if the render fails again, and its Pusher
 * content patch settles both stores, so the optimistic patch only buys the
 * instant flip. Shared by the chip's Retry and any surface that offers one.
 */
export function retryCompositionRender(creativeId: string): Promise<void> {
  // Server-known state, not a human edit: a plain store patch (mirrored into the
  // idea store), never the persisting path, which would PUT the whole content
  // and race the render endpoint's own flag writes.
  useCreativeStore.getState().applyServerContentPatch(creativeId, { compositionRenderFailed: null })
  return renderComposition(creativeId)
    .then(() => undefined)
    .catch((err: unknown) => {
      console.error('composition render retry failed:', err)
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'composition_render',
        step: 'chip_retry',
        creativeId,
      })
    })
}

interface EditsApplyingBadgeProps {
  /**
   * Positions the chip as an overlay for media areas (absolute bottom-2 left-2
   * over the image). Omit for the inline chip. twMerge dedupes conflicting
   * position utilities, so `className` can still override the placement.
   */
  overlay?: boolean
  className?: string
  /**
   * The creative the chip speaks for. With it, the chip reads the live
   * edit-pipeline state from the stores and offers Retry when the render
   * failed; without it the chip is the plain "applying" signal.
   */
  creativeId?: string | null
  /**
   * Explicit failed override for surfaces whose creative is not hydrated in a
   * store (the weekly cockpit reads a server-computed flag). Store state still
   * wins when it says failed.
   */
  failed?: boolean
}

/**
 * Chip shown on master-image surfaces while the server re-renders the flat
 * master from the user's composition edits, and, when that render reached a
 * terminal failure, the honest failed state with a free Retry. The live canvas
 * inside the composer never needs it (it shows the real doc); everywhere else the
 * last rendered image is momentarily stale, and this chip is the signal that the
 * new design is on its way, or that it isn't and what to do about it.
 */
export function EditsApplyingBadge({
  overlay = false,
  className,
  creativeId,
  failed,
}: EditsApplyingBadgeProps) {
  const storeState = useEditsRenderState(creativeId)
  const [retrying, setRetrying] = useState(false)
  const isFailed = !retrying && (storeState === 'failed' || failed === true)

  const handleRetry = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      // The chip sits on cards that open the post on click; Retry is its own action.
      e.preventDefault()
      e.stopPropagation()
      if (!creativeId || retrying) return
      setRetrying(true)
      void retryCompositionRender(creativeId).finally(() => setRetrying(false))
    },
    [creativeId, retrying]
  )

  if (isFailed) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-destructive bg-bg/85 px-2 py-1 text-[11px] font-medium text-paper backdrop-blur-sm',
          overlay && 'absolute bottom-2 left-2 z-20',
          className
        )}
        data-edits-state="failed"
      >
        <AlertTriangle size={13} className="text-destructive" />
        Edits didn't apply
        {creativeId && (
          <button
            type="button"
            onClick={handleRetry}
            className="-my-0.5 -mr-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold text-acid transition-colors hover:text-paper focus-visible:ring-1 focus-visible:ring-acid focus-visible:outline-none"
            aria-label="Retry applying your edits"
          >
            <RotateCw size={11} />
            Retry
          </button>
        )}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'pointer-events-none inline-flex items-center gap-1.5 rounded-full border border-line-bright bg-bg/85 px-2 py-1 text-[11px] font-medium text-paper backdrop-blur-sm',
        overlay && 'absolute bottom-2 left-2 z-20',
        className
      )}
      data-edits-state="applying"
    >
      <Loader2 size={13} className="animate-spin text-acid motion-reduce:animate-none" />
      Applying your edits
    </span>
  )
}
